import { formatLabel } from '../../culture/culture.config';

/// A seller's figures as the pages show them (ShopStatsView on the server): null where there is nothing honest to show
/// yet - a delivered rate under the server's minimum of settled lines, a dispatch time with no dispatches.
export interface ShopStats {
    SellingSince?: string | null;
    ProductsOnSale?: number;
    OrdersDelivered?: number;
    DeliveredRate?: number | null;
    AvgDispatchDays?: number | null;
    RatingAvg?: number | null;
    RatingCount?: number;
    ResponseHours?: number | null;
}

/// One fact of the stats strip: which one, and how it reads.
export interface ShopStatLine {
    key: 'since' | 'delivered' | 'dispatch' | 'rating';
    text: string;
}

/// What puts a fact into the page's words (Multilang_Culture_Spec.md 5.2): the CultureService a component passes in, or
/// the English as written when none is passed.
export interface ShopStatLabels {
    /// The page's language, for the month names (CultureService.lang).
    lang?: string;
    t(key: string, fallback: string, params?: { [name: string]: any }): string;
}

const ENGLISH: ShopStatLabels = { t: (key: string, fallback: string, params?: { [name: string]: any }) => formatLabel(fallback, params) };

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/// Bangla writes the month in full, short or long.
const BN_MONTHS = ['জানুয়ারি', 'ফেব্রুয়ারি', 'মার্চ', 'এপ্রিল', 'মে', 'জুন', 'জুলাই', 'আগস্ট', 'সেপ্টেম্বর', 'অক্টোবর', 'নভেম্বর', 'ডিসেম্বর'];

function months(long: boolean, lang: string): string[] {
    return lang === 'bn' ? BN_MONTHS : long ? LONG_MONTHS : SHORT_MONTHS;
}

/// "Oct 2026" ("October 2026" when `long`) from the date part of an ISO time, so the server render and every browser,
/// whatever its zone, print the same month. Empty for anything that is not a date.
export function monthYear(value: string, long = false, lang = 'en'): string {
    const day = /^(\d{4})-(\d{2})/.exec(String(value || ''));
    const month = day ? +day[2] - 1 : -1;
    if (month < 0 || month > 11) {
        return '';
    }
    return months(long, lang)[month] + ' ' + day[1];
}

/// "13 Oct 2026" from the date part of an ISO time, the same in every zone.
export function dayMonthYear(value: string, lang = 'en'): string {
    const day = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    const month = day ? +day[2] - 1 : -1;
    if (month < 0 || month > 11) {
        return '';
    }
    return (+day[3]) + ' ' + months(false, lang)[month] + ' ' + day[1];
}

/// "4.7": a rating to one decimal.
export function ratingText(value: number): string {
    return (Math.round((Number(value) || 0) * 10) / 10).toFixed(1);
}

/// The facts a shop's stats strip shows, each only when there is one to show: "Selling since Oct 2026", "96% delivered",
/// "Ships in about 2 days", "4.7 out of 5 (31)". The rating needs a rating behind it; the delivered rate and the dispatch
/// time come as null from the server until there is enough to say.
export function shopStatLines(stats: ShopStats, labels: ShopStatLabels = ENGLISH): ShopStatLine[] {
    const lines: ShopStatLine[] = [];
    if (!stats) {
        return lines;
    }

    const since = monthYear(stats.SellingSince, false, labels.lang);
    if (since) {
        lines.push({ key: 'since', text: labels.t('shop-stat-selling-since', 'Selling since {month}', { month: since }) });
    }
    if (stats.DeliveredRate !== null && stats.DeliveredRate !== undefined) {
        lines.push({ key: 'delivered', text: labels.t('shop-stat-delivered', '{rate}% delivered', { rate: stats.DeliveredRate }) });
    }
    if (stats.AvgDispatchDays !== null && stats.AvgDispatchDays !== undefined) {
        // "About" a whole number of days, never "about 0".
        const days = Math.max(1, Math.round(Number(stats.AvgDispatchDays) || 0));
        lines.push({ key: 'dispatch', text: days === 1
            ? labels.t('shop-stat-ships-one-day', 'Ships in about {days} day', { days })
            : labels.t('shop-stat-ships-days', 'Ships in about {days} days', { days }) });
    }
    if (stats.RatingCount > 0 && stats.RatingAvg) {
        lines.push({ key: 'rating', text: labels.t('shop-stat-rating', '{rating} out of 5 ({count})', { rating: ratingText(stats.RatingAvg), count: stats.RatingCount }) });
    }
    return lines;
}

/// One fact of the shop page's chip row on a phone (ShopSetup_UX_Pass.md 8): an icon by its key, the figure, and what it
/// counts.
export interface ShopStatChip {
    key: ShopStatLine['key'];
    value: string;
    label: string;
}

/// The same facts as shopStatLines, split for the chip row: "96%" "delivered", "2 days" "to ship", "4.7" "(31 ratings)".
export function shopStatChips(stats: ShopStats, labels: ShopStatLabels = ENGLISH): ShopStatChip[] {
    const chips: ShopStatChip[] = [];
    if (!stats) {
        return chips;
    }
    const since = monthYear(stats.SellingSince, false, labels.lang);
    if (since) {
        chips.push({ key: 'since', value: since, label: labels.t('shop-chip-selling-since', 'Selling since') });
    }
    if (stats.DeliveredRate !== null && stats.DeliveredRate !== undefined) {
        chips.push({ key: 'delivered', value: stats.DeliveredRate + '%', label: labels.t('shop-chip-delivered', 'delivered') });
    }
    if (stats.AvgDispatchDays !== null && stats.AvgDispatchDays !== undefined) {
        const days = Math.max(1, Math.round(Number(stats.AvgDispatchDays) || 0));
        chips.push({ key: 'dispatch', value: days === 1
            ? labels.t('shop-chip-one-day', '{days} day', { days })
            : labels.t('shop-chip-days', '{days} days', { days }), label: labels.t('shop-chip-to-ship', 'to ship') });
    }
    if (stats.RatingCount > 0 && stats.RatingAvg) {
        chips.push({ key: 'rating', value: ratingText(stats.RatingAvg), label: stats.RatingCount === 1
            ? labels.t('shop-chip-one-rating', '({count} rating)', { count: stats.RatingCount })
            : labels.t('shop-chip-ratings', '({count} ratings)', { count: stats.RatingCount }) });
    }
    return chips;
}
